/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { connect } from 'react-redux';
import * as logger from 'universal-logger';
import * as BooleanUtil from 'helpers/boolean';
import { decorateWith } from 'helpers/decorator';
import { deepExtract } from 'helpers/deep';
import * as ObjectUtil from 'helpers/object';
import { generateOfferContextVariables, formatPrice } from 'helpers/promotions';
import { isAbsent, isEmpty } from 'helpers/verify';
import Base64Deriver from 'derivers/Base64';
import trackDwell from 'decorators/TrackDwell/TrackDwell';
import { withSagas } from 'decorators/Sagas';
import { selectMsaId } from 'redux/modules/features/homepage';
import { selectSelectedOffer } from 'redux/modules/offer';
import { msas } from 'redux/saga/entities/bound';
import { msaByIdentifier } from 'redux/saga/selectors/msas';
import { selectActivePromotion } from 'redux/saga/helpers/promotions';
import { querySelector } from 'redux/saga/selectors/request';
import { ProductGroup } from './ProductGroup';
import { messages } from './ProductGroup.messages';
import { loadSaga, watchSaga, loadCardData, PRODUCT_GROUP_DISCLAIMER_TYPES, PRODUCT_TYPES } from './ProductGroup.saga';

export const COMEMBERSHIPS = {
  BLINKMEMBERSHIP: 'Blink',
  YOUFITMEMBERSHIP: 'YouFit',
};

function formatPlanDetails(intl, plan) {
  const {
    credits_per_cycle: creditsPerCycle,
    max_class_estimate: maxClassEstimate,
    min_class_estimate: minClassEstimate,
  } = plan;

  if ([creditsPerCycle, maxClassEstimate, minClassEstimate].some(isAbsent)) {
    logger.warn(`Incomplete plan data for plan [${plan.id}]`);
    return '';
  }

  // NOTE: non-breaking elements to ensure proper formatting
  return intl.formatMessage(messages.planDetails, {
    creditsPerCycle,
    minClassEstimate,
    maxClassEstimate,
  });
}

export const coMembershipType = offer => {
  const planCode = offer?.recurring_plan?.plan_code || '';
  const coMembershipKeys = Object.keys(COMEMBERSHIPS);
  const coMembershipIndex = coMembershipKeys.findIndex(substring => planCode.includes(substring));
  return coMembershipIndex !== -1 ? COMEMBERSHIPS[coMembershipKeys[coMembershipIndex]] : false;
};

const buildCoMembershipInfo = (intl, offer) => {
  const coMembershipInfo = {};
  coMembershipInfo.type = coMembershipType(offer);
  if (coMembershipInfo.type) {
    coMembershipInfo.banner = intl
      .formatMessage(messages.coMembershipBanner, { coMembershipType: coMembershipInfo.type })
      .toUpperCase();
    coMembershipInfo.details = intl.formatMessage(messages.coMembershipDetails, {
      coMembershipType: coMembershipInfo.type,
    });
  }
  return coMembershipInfo;
};

const buildIndividualOfferDetails = (cardType, disclaimerType, intl, msa, offer) => {
  const initialPrice = formatPrice({
    intl,
    currency: msa.currency_code,
    price: offer.price,
  });
  const recurringPrice = formatPrice({
    intl,
    currency: msa.currency_code,
    price: offer.recurring_plan.price,
  });
  const isSingleMonth = offer.initial_duration === 1 && offer.initial_duration_unit === 'month';
  // Note: backwards compatible for non-required price terms
  const note =
    deepExtract('content.card.price_terms', offer) ||
    (isSingleMonth && intl.formatMessage(messages.firstMonthOfferNote, { recurringPrice })) ||
    '';

  const disclaimerNote =
    disclaimerType === PRODUCT_GROUP_DISCLAIMER_TYPES.autoRenewal
      ? intl.formatMessage(messages.disclaimerAutoRenewal, {
          price: formatPrice({
            intl,
            currency: msa.currency_code,
            price: offer.recurring_plan.price,
            withFractionDigits: true,
          }),
        })
      : '';

  const contextVariables = generateOfferContextVariables({
    offer,
    initialPrice,
    recurringPrice,
  });

  const url =
    cardType && cardType.toLowerCase() === 'signupcard'
      ? `/verify-phone/offer/${Base64Deriver.encode(offer.id)}`
      : `/signup/offer/${Base64Deriver.encode(offer.id)}`;

  return {
    callout: initialPrice,
    calloutNote: note,
    coMembershipInfo: buildCoMembershipInfo(intl, offer),
    contextVariables,
    cta: deepExtract('content.card.cta_copy', offer) || intl.formatMessage(messages.ctaSelect),
    details: formatPlanDetails(intl, offer.initial_plan),
    disclaimer: disclaimerNote,
    productId: offer.id,
    productType: 'offer',
    tag: deepExtract('content.card.tag', offer, ''),
    url,
  };
};

const buildOffersFromPromotion = (cardType, disclaimerType, intl, msa, promotion) => {
  if (isEmpty(promotion.offers)) {
    return {};
  }
  return promotion.offers
    .filter(Boolean)
    .filter(offer => Object.keys(offer).length !== 0)
    .map(offer => buildIndividualOfferDetails(cardType, disclaimerType, intl, msa, offer));
};

const mapCustomCard = (state, props) =>
  ObjectUtil.pick(['callout', 'calloutNote', 'ctaCopy', 'details', 'disclaimer', 'modules', 'tag', 'url'], props);

const mapPromoCard = (state, props) => {
  const { cardType, disclaimerType, intl, productCode } = props;
  const msa = msaByIdentifier(msas.data(state), selectMsaId(state)) || {};
  if (isEmpty(msa)) {
    return {};
  }

  const promotion = selectActivePromotion(state, productCode);
  // TODO: make sure offers is never even populated with null entries
  const offers = buildOffersFromPromotion(cardType, disclaimerType, intl, msa, promotion);

  if (isEmpty(offers)) {
    return {};
  }
  return { offers };
};

export const shouldForceSignupCard = state => BooleanUtil.fromString(querySelector(state).force_signup_card);
// NOTE: Browser detection is tricky.  Full detection support should use a library
export const isSupportedBrowser = state => {
  const userAgent = deepExtract('request.userAgent', state, '');
  return !/\b(?:MSIE|Trident)\b/.test(userAgent);
};

export const mapStateToProps = (state, props) => {
  const cardType =
    // NOTE: provide override while building out support
    (shouldForceSignupCard(state) && 'SignupCard') || (isSupportedBrowser(state) && props.cardType) || 'ProductCard';

  let card;
  let activeDisclaimer;
  let disclaimer;
  const { productType } = props;
  switch (productType) {
    case PRODUCT_TYPES.promotion:
      card = mapPromoCard(state, { ...props, cardType });
      activeDisclaimer =
        selectSelectedOffer(state)?.disclaimer || props.intl.formatMessage(messages.disclaimerAutoRenewalDefault);
      disclaimer = props.showMultipleOffers ? activeDisclaimer : deepExtract('offers.0.disclaimer', card);
      break;

    case PRODUCT_TYPES.custom:
    default:
      // do nothing since the component will just use all the props
      // from the module to populate the UI
      card = mapCustomCard(state, props);
      disclaimer = card.disclaimer; // eslint-disable-line prefer-destructuring
      delete card.disclaimer;
  }

  return {
    card,
    cardType,
    disclaimer,
    msaId: selectMsaId(state),
  };
};

export const mapDispatchToProps = {
  loadCardData,
};

export const ProductGroupContainer = decorateWith(
  trackDwell,
  withSagas({ load: loadSaga, watch: watchSaga }),
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(ProductGroup);

ProductGroupContainer.propTypes = {
  ...ProductGroup.propTypes,
  intl: intlShape.isRequired,
  disclaimerType: PropTypes.oneOf(Object.values(PRODUCT_GROUP_DISCLAIMER_TYPES)),
  productType: PropTypes.oneOf(Object.values(PRODUCT_TYPES)),
  productCode: PropTypes.string,
};
